import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type Dispatch,
  type FocusEvent as ReactFocusEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { useIsMuted, useIsSpeaking, useParticipants, useRoomContext } from '@livekit/components-react'
import { Track, type Participant, type RoomEvent } from 'livekit-client'
import { Clock, Ellipsis, MicOff, UserMinus, UserPlus } from 'lucide-react'
import { Avatar, ConfirmDialog, IconButton, Menu, MenuItem, toast } from '@/components/play'
import { personToneKey } from '@/lib/profile'
import { inviteError, invitesApi, type InviteError } from '@/lib/watch/invitesApi'
import { STAGE_SEATS, initialsSource, nameOrHandle, sentenceName } from '@/lib/watch/rules'
import type { StageFace, StageOwner } from '@/lib/watch/types'
import { useCohostLive } from '@/lib/watch/useCohost'
import { cn } from '@/lib/utils'
import { BadgerAvatar } from './BadgerAvatar'
import { InviteToStageOpener } from './HostInvites'
import { LiveTrack } from './LiveTrack'
import './watch.css'

export interface StageFacesProps {
  /** The stage's live faces in seat order (the room metadata's, or the stage payload's before it arrives). */
  members: StageFace[]
  /** Whose stage, for its lead face; an organisation's stage (the first host leads) without it. */
  owner?: StageOwner
  /** This tab's LiveKit identity: its own face is mirrored and tagged You. */
  localIdentity: string | null
  /**
   * The room is connected (a signal resume counts, as the page's connection says): only then is a seat whose
   * participant isn't in the room away. Before the first connection, and while the room reconnects (LiveKit drops
   * every remote participant), the room has no word on anyone, so nobody is.
   */
  connected: boolean
  /**
   * The stage's id when this viewer may invite people on it (`viewer.can_invite`): the + face follows the faces while
   * seats are free, opening "Invite to your stage". Null or absent for everyone else.
   */
  inviteStageId?: string | null
  /**
   * The stage's id when this viewer hosts it (`viewer.can_host`): each guest's face gets a menu with Remove from stage.
   * Hosts can't be removed here (an organiser removes a person host's whole stage).
   */
  hostStageId?: string | null
  /**
   * The source on the canvas (`useWatchScreen`'s `watchedIdentity`): a host in Studio mode whose camera it is steps
   * aside meanwhile, so their picture plays once.
   */
  watchedIdentity?: string | null
  /** A Studio face was tapped: make it the main video (the viewer's pick, or a host's preview). */
  onWatch?(identity: string): void
  /**
   * Badger's identity while he is on this stage (`useWatchScreen`'s `cohostIdentity`): his face sits after the others,
   * not one of the four seats. Null or absent while he isn't in the room.
   */
  cohostIdentity?: string | null
}

/** Re-render on the room's arrivals and departures (and the connection) only; each face follows its own speaking and
 *  microphone state. useParticipants also re-renders on the local participant's media events (through
 *  useLocalParticipant), which is harmless: the faces are memoised. */
const MEMBERSHIP_ONLY: RoomEvent[] = []

/**
 * Where a face sits, and so its size and tag (watch.css): the stage's owner leads it, at the largest size with the
 * yellow tag; further hosts are co-hosts, a step smaller with a pale yellow tag; guests are smaller still, with an ink
 * tag.
 */
export type StageSeat = 'lead' | 'co-host' | 'guest'

const ORGANIZATION_STAGE: StageOwner = { kind: 'organization', userId: null }

/**
 * The faces in the order they sit, each with its seat. The lead is the stage's owner (the founder, 19 October 2026): on
 * a person's stage, that person, wherever they are in seat order (none while they're off it); on an organisation's,
 * its first live member, the first host in seat order (the owner stage's hosts are the organisation's members). Then
 * the other hosts as co-hosts, then guests, each in seat order (the order they went live). Seat order, not presence: a
 * lead who is away keeps the seat (dimmed) until the API frees it.
 */
export function seatFaces(members: readonly StageFace[], owner: StageOwner = ORGANIZATION_STAGE): { member: StageFace; seat: StageSeat }[] {
  const hosts = members.filter((member) => member.role === 'host')
  const lead =
    owner.kind === 'person'
      ? (owner.userId ? members.find((member) => member.userId === owner.userId) : undefined)
      : hosts[0]
  const coHosts = hosts.filter((member) => member !== lead)
  const guests = members.filter((member) => member.role === 'guest' && member !== lead)
  return [
    ...(lead ? [{ member: lead, seat: 'lead' as const }] : []),
    ...coHosts.map((member) => ({ member, seat: 'co-host' as const })),
    ...guests.map((member) => ({ member, seat: 'guest' as const })),
  ]
}

/** Which face is pinned (a tap, a click, Enter or Space) and which is under a mouse or pen, by identity. */
interface Zoom {
  pinned: string | null
  hovered: string | null
}

type ZoomAction =
  | { type: 'toggle' | 'unpin' | 'hover' | 'unhover'; identity: string }
  /** Escape: no face zoomed, pinned or hovered, until the pointer next comes over one. */
  | { type: 'dismiss' }
  /** The stage's members now: a face that left takes its zoom with it (it never sees the pointer leave). */
  | { type: 'keep'; identities: string[] }

const NO_ZOOM: Zoom = { pinned: null, hovered: null }

/**
 * One face zoomed at a time, the latest wins: pinning a face drops another's hover zoom, and the pointer zooming a
 * face returns another that was pinned. A face's own pin and hover sit together (a click under the mouse).
 */
function zoomReducer(zoom: Zoom, action: ZoomAction): Zoom {
  switch (action.type) {
    case 'toggle':
      if (zoom.pinned === action.identity) return { ...zoom, pinned: null }
      return { pinned: action.identity, hovered: zoom.hovered === action.identity ? zoom.hovered : null }
    case 'unpin':
      return zoom.pinned === action.identity ? { ...zoom, pinned: null } : zoom
    case 'hover':
      if (zoom.hovered === action.identity) return zoom
      return { hovered: action.identity, pinned: zoom.pinned === action.identity ? zoom.pinned : null }
    case 'unhover':
      return zoom.hovered === action.identity ? { ...zoom, hovered: null } : zoom
    case 'dismiss':
      return zoom.pinned === null && zoom.hovered === null ? zoom : NO_ZOOM
    case 'keep': {
      const present = (identity: string | null) => (identity !== null && action.identities.includes(identity) ? identity : null)
      const kept = { pinned: present(zoom.pinned), hovered: present(zoom.hovered) }
      return kept.pinned === zoom.pinned && kept.hovered === zoom.hovered ? zoom : kept
    }
  }
}

/**
 * The stage's talking heads, for the canvas's bottom-left slot: the stage's owner leading, then the other hosts, then
 * guests, in seat order (the order they went live; `seatFaces`). A face shows the member's camera while LiveKit has it
 * and their initials otherwise, a green ring while they speak (the face easing up a tenth, where motion is welcome) and a small quiet
 * mic-off glyph while their microphone is off. A seat whose participant isn't in the connected room (a closed tab the
 * API hasn't freed yet, which lingers a minute or more, or someone not back yet) is away: it keeps its place with its
 * initials on the neutral disc in a grey ring, a small clock where the mic glyph goes, never video, "away" in its name
 * and "Away" on its zoomed tag, so the faces agree with the chat
 * head's "N on stage", which counts only
 * those present. Faces scale with the canvas (watch.css). A name that draws nothing shows the handle (`nameOrHandle`).
 *
 * Each face is one toggle button, named for the person and their role ("Shane Murphy, host"; "You, Lisa Wong, guest"
 * on the viewer's own; "away" after it), and described by what pressing does (Zoom): a tap, a click, Enter or Space
 * zooms (pins) it so viewers can see the speaker, its tag showing their full name and role, the faces after it moving
 * aside so none is stuck behind it, and again (or Escape, focus
 * leaving, a press anywhere else) returns it. A mouse or pen zooms it while over its seat (never a touch); Escape
 * dismisses that too, until the pointer leaves. An Escape that returns a face is used up (`preventDefault`). One face is zoomed at a time. What LiveKit says of them (speaking,
 * microphone off) is in words beside the button, read when browsing the list: in the name, a screen reader would
 * announce the focused face again at each of LiveKit's speaking updates (about every 500 ms).
 *
 * A host in Studio mode is the same face as a 16:9 rounded picture instead of a circle, in their seat, with its tag,
 * ring and glyphs. It doesn't zoom: a tap, a click, Enter or Space makes it the main video (`onWatch`, described as
 * Watch), and while the canvas shows it the face steps aside, so the picture plays once; another camera on the canvas
 * brings it back.
 *
 * Faces never play sound: the provider's audio renderer plays remote voices, and the viewer's own video is muted
 * (LiveTrack) and mirrored.
 *
 * Badger, while he is on the stage (`cohostIdentity`), sits after everyone else (docs/features/badger-cohost.md,
 * Hearing and being heard): his plush face at a co-host's size with his tag, the green ring while he speaks, the mic
 * glyph while he is muted (`BadgerAvatar`). He takes none of the four seats (the + face counts members only), and he
 * doesn't zoom: there is no camera to see closer, so his face is no button, just his name ("Badger") and what LiveKit
 * says of him beside it.
 */
export function StageFaces({
  members,
  owner,
  localIdentity,
  connected,
  inviteStageId = null,
  hostStageId = null,
  watchedIdentity = null,
  onWatch,
  cohostIdentity = null,
}: StageFacesProps) {
  // The trigger only: members are read from the room, which holds each arrival before announcing it.
  useParticipants({ updateOnlyOn: MEMBERSHIP_ONLY })
  const room = useRoomContext()
  const [zoom, dispatch] = useReducer(zoomReducer, NO_ZOOM)
  const rowRef = useRef<HTMLDivElement>(null)
  const [removal, setRemoval] = useState<Removal>(NO_REMOVAL)
  // The stage the removal is asked on, kept with it: a stage switched meanwhile mustn't take the call.
  const hostStageRef = useRef(hostStageId)
  hostStageRef.current = hostStageId
  const onRemove = useCallback(
    (member: StageFace, returnTo: HTMLElement | null) => setRemoval({ open: true, member, returnTo, stageId: hostStageRef.current, failure: null }),
    [],
  )
  const onRowFocus = useFocusKeeper(rowRef)
  // The + face's panel is open: the + face stays while it is, even once the last seat is taken (closing it under the
  // host's hands would lose the link they were copying); it goes when the panel closes.
  const [inviteOpen, setInviteOpen] = useState(false)
  if (inviteOpen && !inviteStageId) setInviteOpen(false)
  // A member who left the stage takes their zoom with them, so they come back unzoomed (an update during render).
  const identities = members.map((member) => member.identity)
  if (zoomReducer(zoom, { type: 'keep', identities }) !== zoom) dispatch({ type: 'keep', identities })

  // Escape returns the zoomed face wherever focus is (a click needn't focus a button), and dismisses a hover zoom
  // without moving the pointer (WCAG 1.4.13). It marks the key used, so what listens after it (the canvas going back to
  // the map after a jump) leaves it alone, and leaves a key something else used (a menu, a dialog) to that.
  const zoomed = zoom.pinned !== null || zoom.hovered !== null
  useEffect(() => {
    if (!zoomed) return
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return
      event.preventDefault()
      dispatch({ type: 'dismiss' })
    }
    document.addEventListener('keydown', dismissOnEscape)
    return () => document.removeEventListener('keydown', dismissOnEscape)
  }, [zoomed])

  // Badger's participant, by identity (a map lookup): his seat follows it.
  const cohost = cohostIdentity ? (room.getParticipantByIdentity(cohostIdentity) ?? null) : null
  if (members.length === 0 && !cohost) return null

  // A host in Studio mode is a 16:9 face; while the canvas shows their camera it steps aside (their seat and its size
  // stay for the others), so their picture plays once.
  const seated = seatFaces(members, owner).filter(({ member }) => !(member.studio && member.identity === watchedIdentity))
  // Seats as the server counts them for `stage_full`: every live member, an away one too, until the API frees it.
  const seatsFree = members.length < STAGE_SEATS
  const plusFace = Boolean(inviteStageId && (seatsFree || inviteOpen))
  if (seated.length === 0 && !cohost && !plusFace && !removal.open) return null
  const removing = removal.member
  const who = removing ? sentenceName(removing) : ''

  const confirmRemoval = async () => {
    if (!removing || !removal.stageId) return
    try {
      await invitesApi.removeGuest(removal.stageId, removing.userId)
    } catch (failure) {
      const error = inviteError(failure)
      // Gone already (they left, or another host removed them): nothing to remove, and the metadata follows.
      if (error.type === 'not_found') {
        toast({ title: `${who} isn’t on the stage any more` })
        return
      }
      setRemoval((current) => ({ ...current, failure: removalFailure(error, who) }))
      throw failure
    }
  }
  // Cancelled, back to the face's menu button; once their face has gone (the metadata came first), to what's left.
  const removalFinalFocus = () => (removal.returnTo?.isConnected ? removal.returnTo : fallbackFocus(rowRef.current) ?? true)

  return (
    <div ref={rowRef} className="watch-faces__row" {...onRowFocus}>
      {(seated.length > 0 || cohost) && (
        <ul className="watch-faces" aria-label="On stage">
          {seated.map(({ member, seat }) => {
            // The local participant or a remote one, by identity (a map lookup).
            const participant = room.getParticipantByIdentity(member.identity) ?? null
            const local = member.identity === localIdentity
            return (
              <Seat
                key={member.identity}
                member={member}
                seat={seat}
                local={local}
                participant={participant}
                away={connected && participant === null}
                pinned={zoom.pinned === member.identity}
                hovered={zoom.hovered === member.identity}
                dispatch={dispatch}
                onRemove={hostStageId && member.role === 'guest' && !local ? onRemove : undefined}
                onWatch={member.studio ? onWatch : undefined}
              />
            )
          })}
          {/* A new participant object (he rejoined under the same identity) gets fresh subscriptions. */}
          {cohost && <BadgerSeat key={`badger:${cohost.sid}`} participant={cohost} />}
        </ul>
      )}
      {plusFace && inviteStageId && (
        <div className="watch-faces__invite">
          <InviteToStageOpener
            stageId={inviteStageId}
            seatsTaken={members.length}
            onOpenChange={setInviteOpen}
            trigger={
              <IconButton variant="ghost" elevated label="Invite to stage">
                <UserPlus />
              </IconButton>
            }
          />
        </div>
      )}
      <ConfirmDialog
        open={removal.open}
        onOpenChange={(open) => setRemoval((current) => ({ ...current, open }))}
        title={`Remove ${who} from the stage?`}
        description="Their camera and microphone go off the stage. They can still watch and chat."
        confirmLabel="Remove from stage"
        failureMessage={removal.failure ?? `Couldn’t remove ${who}. Try again.`}
        onConfirm={confirmRemoval}
        finalFocus={removalFinalFocus}
      />
    </div>
  )
}

/** A guest's removal: asked in a dialog, from the face's menu button (`returnTo`), and why it failed. */
interface Removal {
  open: boolean
  member: StageFace | null
  returnTo: HTMLElement | null
  /** The stage it was asked on. */
  stageId: string | null
  failure: string | null
}

const NO_REMOVAL: Removal = { open: false, member: null, returnTo: null, stageId: null, failure: null }

/** Why a removal didn't happen, in the dialog: what to do about the connection or a throttle, else try again. */
function removalFailure(error: InviteError, who: string): string {
  if (error.type === 'network' || error.type === 'rate_limited' || error.type === 'forbidden' || error.type === 'unauthorized') {
    return error.message
  }
  return `Couldn’t remove ${who}. Try again.`
}

/** Where focus goes when the face that had it leaves the stage: the + face, else the last face left. */
function fallbackFocus(row: HTMLElement | null): HTMLElement | null {
  if (!row) return null
  const faces = row.querySelectorAll<HTMLElement>('.watch-face__zoom')
  return row.querySelector<HTMLElement>('.watch-faces__invite button') ?? faces[faces.length - 1] ?? null
}

/**
 * Keeps keyboard focus on the faces when the face that had it leaves the stage (a guest removed, a seat freed): React
 * fires no blur for a removed button, and focus would drop to the page. Remembers what had focus in the row until focus
 * moves on to something that is still there; after a render that took it away with nothing else focused, focus goes to
 * the + face or the last face left (`fallbackFocus`). Returns the row's focus handlers.
 */
function useFocusKeeper(rowRef: RefObject<HTMLElement | null>) {
  const focusedRef = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const focused = focusedRef.current
    if (!focused || focused.isConnected) return
    const active = document.activeElement
    if (active && active !== document.body) return
    const next = fallbackFocus(rowRef.current)
    focusedRef.current = next
    next?.focus({ preventScroll: true })
  })
  return {
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      focusedRef.current = event.target
    },
    onBlur: (event: ReactFocusEvent<HTMLElement>) => {
      const target = event.target
      // Moved on to something still there: forget it. A removed control keeps it, for the effect.
      queueMicrotask(() => {
        if (focusedRef.current === target && target.isConnected && !rowRef.current?.contains(document.activeElement)) {
          focusedRef.current = null
        }
      })
    },
  }
}

/** What LiveKit says of the participant behind a seat, and which participant said it. */
interface LiveState {
  sid: string
  speaking: boolean
  micOff: boolean
}

interface SeatProps {
  member: StageFace
  seat: StageSeat
  local: boolean
  participant: Participant | null
  away: boolean
  pinned: boolean
  hovered: boolean
  dispatch: Dispatch<ZoomAction>
  /** This viewer hosts the stage and this is a guest's seat: the face gets a menu with Remove from stage. */
  onRemove?: (member: StageFace, returnTo: HTMLElement | null) => void
  /** A host in Studio mode: their 16:9 face makes them the main video when tapped, rather than zooming. */
  onWatch?: (identity: string) => void
}

/**
 * A seat stays put when its participant arrives, leaves or rejoins, and so does its face: the face (and its button,
 * with any focus) is one element throughout, and only the live state beside it is keyed to the participant.
 *
 * The seat, not the face, follows a mouse or pen (never a touch): its own circle (watch.css) stays the face's size at
 * rest while the face zooms 1.6 times from its ring's bottom left, up and right off the circle's lower-left edge. Over
 * the face's button the pointer would leave as the zoom grew away from under it, the face would shrink back under it
 * and zoom again; over the seat it leaves only once it is off both the circle and the zoomed face.
 */
function Seat({ member, seat, local, participant, away, pinned, hovered, dispatch, onRemove, onWatch }: SeatProps) {
  const [live, setLive] = useState<LiveState | null>(null)
  // Only the participant now behind the seat counts: nothing before they arrive or after they leave, and a rejoin's own.
  const state = participant && live?.sid === participant.sid ? live : null
  const { identity } = member
  // A Studio face doesn't zoom: a tap makes it the main video.
  const studio = Boolean(member.studio)
  const follow = (type: 'hover' | 'unhover') => (event: ReactPointerEvent) => {
    if (event.pointerType !== 'touch' && !studio) dispatch({ type, identity })
  }
  return (
    <li className="watch-faces__seat" data-studio={studio || undefined} onPointerEnter={follow('hover')} onPointerLeave={follow('unhover')}>
      {/* A new participant object (a rejoin under the same identity) gets fresh subscriptions. */}
      {participant && <FollowParticipant key={participant.sid} participant={participant} onChange={setLive} />}
      <Face
        member={member}
        seat={seat}
        local={local}
        speaking={state?.speaking ?? false}
        micOff={state?.micOff ?? false}
        away={away}
        pinned={pinned && !studio}
        hovered={hovered && !studio}
        dispatch={dispatch}
        onRemove={onRemove}
        onWatch={studio ? onWatch : undefined}
      />
    </li>
  )
}

/**
 * Badger's seat, after the others: his face follows his participant (state, voice, mute) on its own, so the row
 * re-renders only when he comes or goes. Held up a moment after he last spoke, as a speaker's face is: his small bob.
 */
function BadgerSeat({ participant }: { participant: Participant }) {
  const live = useCohostLive(participant)
  const lifted = useHeldTrue(live.speaking, LIFT_HOLD_MS)
  return (
    <li className="watch-faces__seat" data-badger="">
      <BadgerAvatar state={live.state} speaking={live.speaking} muted={live.muted} track={live.track} lifted={lifted} />
    </li>
  )
}

/** A face's picture: their camera (mirrored for the viewer's own), or their initials while they have none or are away. */
function FacePicture({ member, local, away, initials }: { member: StageFace; local: boolean; away: boolean; initials: ReactNode }) {
  return (
    <span className="watch-face__picture" aria-hidden="true">
      {/* Away, the initials alone: nothing of theirs is live, whatever picture lingers for the identity. */}
      {away ? (
        initials
      ) : (
        <LiveTrack identity={member.identity} className={cn('watch-face__video', local && 'watch-face__video--mirror')} fallback={initials} />
      )}
    </span>
  )
}

/** Follows a participant's speaking and microphone for its seat: the hooks only run with a participant to follow. */
function FollowParticipant({ participant, onChange }: { participant: Participant; onChange: (state: LiveState) => void }) {
  const speaking = useIsSpeaking(participant)
  const micOff = useIsMuted({ participant, source: Track.Source.Microphone })
  const { sid } = participant
  // Before paint, so the face never shows a frame of the previous participant's state.
  useLayoutEffect(() => {
    onChange({ sid, speaking, micOff })
  }, [sid, speaking, micOff, onChange])
  return null
}

function displayName(member: StageFace) {
  return nameOrHandle(member.name, member.handle, member.role === 'host' ? 'Host' : 'Guest')
}

/** The tag fits under a face beside its neighbours': the first name, as the prototype's. */
function tagName(member: StageFace, local: boolean) {
  if (local) return 'You'
  return displayName(member).split(/\s+/)[0]
}

/** A zoomed face's tag: the full name (or You) and the role, "Shane Murphy · Host"; away, "Lisa Wong · Away" instead. */
function fullTag(member: StageFace, { local, away }: { local: boolean; away: boolean }) {
  const role = away ? 'Away' : member.role === 'host' ? 'Host' : 'Guest'
  return `${local ? 'You' : displayName(member)} · ${role}`
}

/**
 * The face's one accessible name, steady while LiveKit's state changes: the person and their role, "host" for the lead
 * and co-hosts alike (the size says which; the words stay short). The viewer's own starts with You, as its tag does
 * (label in name); a seat whose participant isn't in the room ends with away, which changes rarely.
 */
function faceName(member: StageFace, { local, away }: { local: boolean; away: boolean }) {
  return [local && 'You', displayName(member), member.role, away && 'away'].filter(Boolean).join(', ')
}

/** What LiveKit says of them, beside the name: "speaking, microphone off", or nothing. */
function stateWords({ speaking, micOff }: { speaking: boolean; micOff: boolean }) {
  return [speaking && 'speaking', micOff && 'microphone off'].filter(Boolean).join(', ')
}

/** The nearest ancestor that is a size container: the canvas, which the faces' cqi lengths answer to. */
function sizeContainer(from: Element) {
  for (let node = from.parentElement; node; node = node.parentElement) {
    if (/size/.test(getComputedStyle(node).containerType)) return node
  }
  return null
}

/**
 * Whether a zoomed face's seat is past the middle of the canvas (a later seat on a phone), measured on the face's
 * unscaled box (its seat's left, its own width) as it zooms. Its tag, at most half the canvas wide, then ends at the face's right instead of
 * starting at its left, so a long name stays inside the canvas.
 */
function usePastMiddle(faceRef: RefObject<HTMLElement | null>, enlarged: boolean) {
  const [pastMiddle, setPastMiddle] = useState(false)
  useLayoutEffect(() => {
    const face = faceRef.current
    const seat = face?.parentElement
    const canvas = enlarged && seat ? sizeContainer(seat) : null
    if (!face || !seat || !canvas) return
    // The seat's left and the face's own width: a zoomed seat is wider by the room it makes for the faces after it.
    const [left, bounds] = [seat.getBoundingClientRect().left, canvas.getBoundingClientRect()]
    setPastMiddle(left + face.offsetWidth / 2 - bounds.left > bounds.width / 2)
  }, [faceRef, enlarged])
  return enlarged && pastMiddle
}

/**
 * How long a speaker stays lifted after LiveKit last said they spoke. LiveKit sends speaker changes about every 500 ms
 * with no debounce, so a breath between words reads as one update of silence; held for a second, the lift (and the
 * seat's place over its neighbours) rides through it. The ring and "speaking" follow LiveKit as it says.
 */
const LIFT_HOLD_MS = 1000

/** `value`, kept true for `ms` after it last was. */
function useHeldTrue(value: boolean, ms: number) {
  const [held, setHeld] = useState(value)
  if (value && !held) setHeld(true)
  useEffect(() => {
    if (value || !held) return
    const timer = window.setTimeout(() => setHeld(false), ms)
    return () => window.clearTimeout(timer)
  }, [value, held, ms])
  return value || held
}

interface FaceProps {
  member: StageFace
  seat: StageSeat
  local: boolean
  speaking: boolean
  micOff: boolean
  /** The seat's participant isn't in the connected room: initials on the neutral disc in a grey ring with a clock,
   *  never video. */
  away: boolean
  /** Pinned (StageFaces holds it by identity, so it survives the participant arriving). */
  pinned: boolean
  /** Under a mouse or pen over its seat, and not dismissed. A touch never hovers, so a tap zooms by pinning alone. */
  hovered: boolean
  dispatch: Dispatch<ZoomAction>
  /** A guest's face, for a host: its menu (Remove from stage). Stable (StageFaces'), so the memo holds. */
  onRemove?: (member: StageFace, returnTo: HTMLElement | null) => void
  /**
   * A host in Studio mode: a 16:9 face whose press makes them the main video (the page's `select`), instead of the
   * zoom. Stable (the page's), so the memo holds.
   */
  onWatch?: (identity: string) => void
}

const Face = memo(function Face({ member, seat, local, speaking, micOff, away, pinned, hovered, dispatch, onRemove, onWatch }: FaceProps) {
  const faceRef = useRef<HTMLSpanElement>(null)
  const menuRef = useRef<HTMLButtonElement>(null)
  const lifted = useHeldTrue(speaking, LIFT_HOLD_MS)
  const enlarged = pinned || hovered
  const tagEnd = usePastMiddle(faceRef, enlarged)
  const { identity } = member
  const studio = Boolean(member.studio)

  // A touch screen moves no focus, so blur alone can't return a tapped face: any press outside it does. In the capture
  // phase, so a press that stops its own propagation (the map, a menu) still counts.
  useEffect(() => {
    if (!pinned) return
    const returnOnPressElsewhere = (event: PointerEvent) => {
      if (!faceRef.current?.contains(event.target as Node)) dispatch({ type: 'unpin', identity })
    }
    document.addEventListener('pointerdown', returnOnPressElsewhere, true)
    return () => document.removeEventListener('pointerdown', returnOnPressElsewhere, true)
  }, [pinned, identity, dispatch])

  const words = stateWords({ speaking, micOff })
  // Toned as the person's chat messages are (`personToneKey`): a face without video reads as theirs, not a pale grey disc.
  // Away, Play's neutral disc instead (its initials at AA), inside a grey ring (watch.css): apart from everyone present.
  const toneKey = personToneKey({ handle: member.handle, id: member.userId })
  const initials = (
    <Avatar
      name={initialsSource(member.name, member.handle)}
      {...(away ? { tone: 'neutral' as const } : { tone: 'auto' as const, toneKey })}
      className="watch-face__avatar"
    />
  )
  return (
    <span
      ref={faceRef}
      className="watch-face"
      data-seat={seat}
      data-role={member.role}
      data-local={local || undefined}
      data-speaking={speaking}
      data-away={away || undefined}
      data-lifted={lifted || undefined}
      data-zoomed={enlarged || undefined}
      data-tag-end={tagEnd || undefined}
      data-studio={studio || undefined}
    >
      <span className="watch-face__ring" aria-hidden="true" />
      {/* The picture is the zoom toggle (a Studio face's, the way to the main video), and the face's one name: who and
          their role. */}
      {studio ? (
        <button
          type="button"
          className="watch-face__zoom"
          aria-label={faceName(member, { local, away })}
          aria-description="Watch"
          onClick={() => onWatch?.(identity)}
        >
          <FacePicture member={member} local={local} away={away} initials={initials} />
        </button>
      ) : (
        <button
          type="button"
          className="watch-face__zoom"
          aria-label={faceName(member, { local, away })}
          aria-description="Zoom"
          aria-pressed={pinned}
          onClick={() => dispatch({ type: 'toggle', identity })}
          // Focus moving on to the face's own menu button keeps the zoom: the menu belongs to the zoomed face.
          onBlur={(event) => {
            if (!faceRef.current?.contains(event.relatedTarget as Node | null)) dispatch({ type: 'unpin', identity })
          }}
        >
          <FacePicture member={member} local={local} away={away} initials={initials} />
        </button>
      )}
      {/* Read after the name when browsing the list, and never announced on its own (not a live region). */}
      {words && <span className="sr-only">{words}</span>}
      {/* The words beside the button say it; the glyph is its quiet visible form. */}
      {micOff && (
        <span className="watch-face__glyph watch-face__mic-off" aria-hidden="true">
          <MicOff />
        </span>
      )}
      {/* Away has more than its grey: a clock in the same slot (an away seat follows no microphone), as the name says. */}
      {away && (
        <span className="watch-face__glyph watch-face__away" aria-hidden="true">
          <Clock />
        </span>
      )}
      {/* The button's name says the full name; the tag is its visible form, the first name until the face zooms. */}
      <span className="watch-face__tag" aria-hidden="true">
        {enlarged ? fullTag(member, { local, away }) : tagName(member, local)}
      </span>
      {/* A host's way to take a guest off the stage: a second control beside the face's one name, shown once the face
          zooms (a tap or a hover), while it has keyboard focus, or while its menu is open (watch.css). */}
      {onRemove && (
        <Menu
          side="top"
          align="start"
          trigger={
            <IconButton ref={menuRef} variant="ghost" elevated size="compact" label={`Manage ${sentenceName(member)}`} className="watch-face__menu">
              <Ellipsis />
            </IconButton>
          }
        >
          <MenuItem icon={<UserMinus />} onClick={() => onRemove(member, menuRef.current)}>
            Remove from stage
          </MenuItem>
        </Menu>
      )}
    </span>
  )
})
